import type { InputCommand } from './protocol.js';

/** Scroll distance as a fraction of screen height per wheel notch. */
const SCROLL_FRACTION = 0.35;
const SCROLL_DURATION_MS = 120;

type Send = (command: InputCommand) => void;

/** The source stream's pixel size, used to work out the letterboxed content box. */
type SourceSize = () => { width: number; height: number };

/**
 * Translates browser pointer, wheel and keyboard events into [InputCommand]s.
 *
 * Works against whatever element renders the stream: a `<video>` in MediaProjection
 * mode or a `<canvas>` in scrcpy mode. The caller supplies the source frame size, since
 * a canvas has no videoWidth/videoHeight of its own.
 *
 * Two things this has to get right:
 *
 * 1. **Letterboxing.** The element is sized by CSS and the stream keeps its own aspect
 *    ratio, so contain-fit leaves bars on one axis. getBoundingClientRect alone would
 *    map clicks in the bars onto real screen coordinates and offset every touch.
 *    normalize() works out the drawn content box from the source size instead.
 *
 * 2. **Move rate.** A mouse fires far more pointermove events than the phone can
 *    usefully dispatch, and each one is a gesture continuation on the far end.
 *    Moves are coalesced to one per animation frame.
 */
export function attachInput(target: HTMLElement, sourceSize: SourceSize, send: Send): () => void {
  let dragging = false;
  let pendingMove: { x: number; y: number } | null = null;
  let rafHandle = 0;

  function normalize(event: { clientX: number; clientY: number }): { x: number; y: number } | null {
    const rect = target.getBoundingClientRect();
    const { width: sourceWidth, height: sourceHeight } = sourceSize();
    if (!sourceWidth || !sourceHeight || !rect.width || !rect.height) return null;

    // Work out the letterboxed content box inside the element.
    const scale = Math.min(rect.width / sourceWidth, rect.height / sourceHeight);
    const drawnWidth = sourceWidth * scale;
    const drawnHeight = sourceHeight * scale;
    const offsetX = (rect.width - drawnWidth) / 2;
    const offsetY = (rect.height - drawnHeight) / 2;

    const x = (event.clientX - rect.left - offsetX) / drawnWidth;
    const y = (event.clientY - rect.top - offsetY) / drawnHeight;

    // A click in the letterbox bars has no corresponding point on the phone.
    if (x < 0 || x > 1 || y < 0 || y > 1) return null;
    return { x, y };
  }

  function flushMove(): void {
    rafHandle = 0;
    if (!pendingMove || !dragging) return;
    send({ t: 'pointer', a: 'move', x: pendingMove.x, y: pendingMove.y });
    pendingMove = null;
  }

  const onPointerDown = (event: PointerEvent): void => {
    if (event.button !== 0) return;
    const point = normalize(event);
    if (!point) return;

    event.preventDefault();
    // preventDefault suppresses the browser's focus-on-click, so focus the surface
    // explicitly. Otherwise, once focus leaves it (e.g. clicking a nav button), keyboard
    // events stop arriving and typing silently dies until the session is remade.
    target.focus();
    // Capture so a drag that leaves the element still delivers move and up here.
    target.setPointerCapture(event.pointerId);
    dragging = true;
    send({ t: 'pointer', a: 'down', x: point.x, y: point.y });
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (!dragging) return;
    const point = normalize(event);
    if (!point) return;

    pendingMove = point;
    if (rafHandle === 0) rafHandle = requestAnimationFrame(flushMove);
  };

  const endDrag = (event: PointerEvent): void => {
    if (!dragging) return;
    dragging = false;

    if (rafHandle !== 0) {
      cancelAnimationFrame(rafHandle);
      rafHandle = 0;
    }
    pendingMove = null;

    if (target.hasPointerCapture(event.pointerId)) {
      target.releasePointerCapture(event.pointerId);
    }

    // Clamp rather than drop: a pointerup outside the frame still has to lift the
    // finger, or the phone is left mid-drag until the stroke times out.
    const point = normalize(event) ?? clampToEdge(event, target);
    send({ t: 'pointer', a: 'up', x: point.x, y: point.y });
  };

  const onWheel = (event: WheelEvent): void => {
    const point = normalize(event);
    if (!point) return;
    event.preventDefault();

    // Android has no wheel concept for touch, so a scroll becomes a swipe in the
    // opposite direction: wheel down means content moves up, i.e. swipe up.
    const direction = event.deltaY > 0 ? -1 : 1;
    const endY = clamp01(point.y + direction * SCROLL_FRACTION);

    send({
      t: 'scroll',
      x: point.x,
      y: point.y,
      x2: point.x,
      y2: endY,
      d: SCROLL_DURATION_MS,
    });
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;

    if (event.key === 'Backspace') {
      event.preventDefault();
      send({ t: 'text', a: 'backspace' });
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      send({ t: 'key', a: 'back' });
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      send({ t: 'text', v: '\n' });
      return;
    }

    // Single printable characters only. Named keys ("Shift", "ArrowLeft") are
    // multi-character and have no AccessibilityService equivalent.
    if (event.key.length === 1) {
      event.preventDefault();
      send({ t: 'text', v: event.key });
    }
  };

  target.addEventListener('pointerdown', onPointerDown);
  target.addEventListener('pointermove', onPointerMove);
  target.addEventListener('pointerup', endDrag);
  target.addEventListener('pointercancel', endDrag);
  target.addEventListener('wheel', onWheel, { passive: false });
  target.addEventListener('keydown', onKeyDown);
  target.addEventListener('contextmenu', (e) => e.preventDefault());

  return () => {
    target.removeEventListener('pointerdown', onPointerDown);
    target.removeEventListener('pointermove', onPointerMove);
    target.removeEventListener('pointerup', endDrag);
    target.removeEventListener('pointercancel', endDrag);
    target.removeEventListener('wheel', onWheel);
    target.removeEventListener('keydown', onKeyDown);
    if (rafHandle !== 0) cancelAnimationFrame(rafHandle);
  };
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/** Nearest in-frame point for a pointer that ended outside the render element. */
function clampToEdge(
  event: { clientX: number; clientY: number },
  target: HTMLElement,
): { x: number; y: number } {
  const rect = target.getBoundingClientRect();
  return {
    x: clamp01((event.clientX - rect.left) / rect.width),
    y: clamp01((event.clientY - rect.top) / rect.height),
  };
}
